// src/domains/live/store/liveStore.js
import { defineStore } from 'pinia'
import * as liveService from '../services/liveService'
import * as liveKit from '../services/liveKitService'
import { parseLiveError } from '../services/liveErrors'
import { liveSocketKeepAlive } from '../services/liveSocketKeepAlive'
import { LIVE_DEFAULTS, LIVE_END_REASONS, LIVE_PHASES, LIVE_TIMEOUTS } from '../constants/liveConfig'
import WebSocketManager from '@/shared/services/WebSocketManager'
import { userStore as useUserStore } from '@/shared/store/userStore'
import { buildHostInfoFromStream, normalizeViewerListItem } from '../utils/liveHostUtils'
import { normalizeFollowingLiveItems } from '../utils/liveStoryFeed'
import {
  commentMatchesPendingOptimistic,
  dedupeCommentsList,
  mergeIncomingComment,
} from '../utils/liveCommentUtils'

function createInitialState() {
  return {
    phase: LIVE_PHASES.IDLE,
    liveStream: null,
    role: null,
    token: null,
    wsUrl: null,
    hostInfo: null,
    viewerCount: 0,
    likeCount: 0,
    commentCount: 0,
    comments: [],
    pinnedComment: null,
    blockedUserIds: new Set(),
    isAudioMuted: false,
    isVideoOff: false,
    isHostVideoOff: false,
    // True when the browser blocked remote-audio autoplay → show "tap for sound".
    audioBlocked: false,
    startedAt: null,
    endStats: null,
    endReason: null,
    isMuted: false,
    mutedRemainingSeconds: 0,
    feedItems: [],
    followingLiveItems: [],
    feedLoading: false,
    feedPagination: { page: 1, hasMore: false },
    feedSort: 'viewers',
    feedCategory: null,
    hasServerActiveLive: false,
    recoverableLive: null,
    error: null,
    recentJoins: [],
    floatingReactions: [],
    videoLayoutEpoch: 0,
    postAsPageId: null,
    postAsPageName: null,
    postAsPageAvatar: null,
    privacySetting: 'PUBLIC',
    pendingNavigation: null,
    guestInvite: null,
    speakRequests: [],
    speakRequestPending: false,
    viewerListRevision: 0,
    guestMedia: {},
    // userId -> { displayName, username, profilePictureUrl } for on-stage guest
    // tile labels. Seeded from join/detail payloads and live guest events.
    guestProfiles: {},
    focusedUserId: null,
    forceMutedNotice: null,
    isHostingSession: false,
    _pendingComments: [],
    _pendingSettings: null,
    _settingsRollback: null,
    _settingsTimer: null,
    _settingsFlushPromise: null,
    _reconnectTimer: null,
    _pendingCommentRetryTimer: null,
    _pendingCommentRetryAttempt: 0,
    _lastReactionAt: 0,
    _reactionDisplayAt: {},
    _socketHandlersBound: false,
    _liveKitUnsubs: [],
    _socketStateUnsub: null,
    _recentEventIds: [],
    _socketHandlerRefs: null,
    _tokenRefreshTimer: null,
    hostLiveMode: 'VIDEO',
    liveCategory: 'CHAT',
    maxDurationWarning: null,
    milestoneMessage: null,
    activeGuests: [],
    _lastTypingAt: 0,
    _suspendedStream: null,
  }
}

export const useLiveStore = defineStore('live', {
  state: () => createInitialState(),

  getters: {
    isHost: (s) => s.phase === LIVE_PHASES.CONNECTED_HOST,
    isViewer: (s) => s.phase === LIVE_PHASES.CONNECTED_VIEWER,
    isInSession: (s) =>
      [
        LIVE_PHASES.SOCKET_CONNECTING,
        LIVE_PHASES.LIVEKIT_CONNECTING,
        LIVE_PHASES.CONNECTING,
        LIVE_PHASES.CONNECTED_HOST,
        LIVE_PHASES.CONNECTED_VIEWER,
        LIVE_PHASES.RECONNECTING,
      ].includes(s.phase),
    liveStreamId: (s) => s.liveStream?.id ?? null,
    streamStatus: (s) => s.liveStream?.status ?? null,
    // LiveKit identity of the host's video feed. CAMERA lives: the streamer's
    // userId (the client publishes with identity = userId). OBS lives: the host
    // video comes from the ingress participant `obs:<liveStreamId>`. Used to pin
    // the main stage on the host and to exclude the host from the guest tiles.
    hostParticipantIdentity: (s) => {
      const live = s.liveStream
      if (!live?.id) return null
      if (live.sourceType === 'OBS') return `obs:${live.id}`
      return live.userId ?? null
    },
    commentsEnabled: (s) => s.liveStream?.commentsEnabled ?? true,
    hostUser: (s) => s.liveStream?.user ?? (s.hostInfo?.type === 'user' ? s.hostInfo : null),
    hasActiveLive: (s) =>
      s.hasServerActiveLive ||
      s.phase === LIVE_PHASES.CONNECTED_HOST ||
      s.phase === LIVE_PHASES.RECOVERING,
    canResumeHostLive: (s) =>
      s.hasServerActiveLive &&
      !s.isHostingSession &&
      [LIVE_PHASES.IDLE, LIVE_PHASES.PREVIEWING].includes(s.phase),
    hostTimerStartedAt: (s) => {
      const status = s.liveStream?.status
      const raw =
        s.startedAt ||
        s.liveStream?.startedAt ||
        (['LIVE', 'RECONNECTING'].includes(status) ? s.liveStream?.createdAt : null)
      if (!raw) return null
      return typeof raw === 'string' ? raw : new Date(raw).toISOString()
    },
    liveUserMap: (s) => {
      const map = {}
      for (const item of s.followingLiveItems) {
        const entityId = item.pageId || item.userId
        if (entityId) map[entityId] = item.liveStreamId
      }
      return map
    },
  },

  actions: {
    _setPhase(phase) {
      this.phase = phase
      liveSocketKeepAlive.syncFromPhase(phase)
      if (phase === LIVE_PHASES.CONNECTED_HOST) {
        this._startTokenRefresh()
      } else if (phase === LIVE_PHASES.ENDED || phase === LIVE_PHASES.IDLE || phase === LIVE_PHASES.ERROR) {
        this._stopTokenRefresh()
      }
    },

    _dedupSocketEvent(data, eventName) {
      const eventId = data?.eventId
      if (eventId) {
        if (this._recentEventIds.includes(eventId)) return true
        this._recentEventIds = [...this._recentEventIds.slice(-63), eventId]
        return false
      }
      if (eventName === 'live:comment:new') {
        const commentId = data?.comment?.id ?? data?.id
        if (commentId) {
          const key = `live:comment:new|${commentId}`
          if (this._recentEventIds.includes(key)) return true
          this._recentEventIds = [...this._recentEventIds.slice(-63), key]
        }
        return false
      }
      if (eventName === 'live:started' || eventName === 'live:ended') {
        const key = `${eventName}|${data?.liveStreamId}|${data?.timestamp ?? data?.endedAt ?? ''}`
        if (this._recentEventIds.includes(key)) return true
        this._recentEventIds = [...this._recentEventIds.slice(-63), key]
      }
      return false
    },

    _commentMatchesPendingOptimistic(pending, comment) {
      const me = useUserStore().getUser()?.id
      return commentMatchesPendingOptimistic(pending, comment, me)
    },

    _removePendingCommentPayload(content, liveStreamId) {
      const streamId = liveStreamId ?? this.liveStream?.id
      if (!streamId) return
      this._pendingComments = this._pendingComments.filter(
        (p) => !(p.content === content && p.liveStreamId === streamId),
      )
    },

    _dedupeCommentsList() {
      this.comments = dedupeCommentsList(
        this.comments,
        LIVE_DEFAULTS.maxChatMessages,
        (pending, comment) => this._commentMatchesPendingOptimistic(pending, comment),
      )
    },

    _mergeIncomingComment(comment, meta = {}) {
      const me = useUserStore().getUser()?.id
      const beforeLen = this.comments.length
      this.comments = mergeIncomingComment(this.comments, comment, meta, {
        maxMessages: LIVE_DEFAULTS.maxChatMessages,
        currentUserId: me,
      })
      if (this.comments.length !== beforeLen || this.comments.some((c) => c.id === comment.id)) {
        this._removePendingCommentPayload(comment.content, comment.liveStreamId)
      }
    },

    _startTokenRefresh() {
      this._stopTokenRefresh()
      if (!this.isHostingSession || !this.liveStream?.id) return
      const interval = LIVE_TIMEOUTS.tokenRefreshIntervalMs
      this._tokenRefreshTimer = setInterval(async () => {
        if (this.phase !== LIVE_PHASES.CONNECTED_HOST && this.phase !== LIVE_PHASES.RECONNECTING) return
        const id = this.liveStream?.id
        if (!id) return
        try {
          const res = await liveService.refreshStreamerToken(id)
          const { token, wsUrl } = res.data
          if (token && wsUrl) {
            this.token = token
            this.wsUrl = wsUrl
            await liveKit.disconnectLiveKit()
            const audioOnly = this.hostLiveMode === 'AUDIO_ONLY'
            await liveKit.connectLiveKitAsHost(wsUrl, token, { audioOnly })
            this._registerLiveKitListeners()
            this.videoLayoutEpoch += 1
          }
        } catch { /* */ }
      }, interval)
    },

    _stopTokenRefresh() {
      if (this._tokenRefreshTimer) {
        clearInterval(this._tokenRefreshTimer)
        this._tokenRefreshTimer = null
      }
    },

  // ─── Recovery ────────────────────────────────────────────────────────
    async checkForActiveLive() {
      try {
        const res = await liveService.getMyCurrentLive()
        const live = res.data?.live
        const isActive = live != null && ['WAITING', 'LIVE', 'RECONNECTING'].includes(live.status)
        this.hasServerActiveLive = isActive
        this.recoverableLive = isActive ? live : null
        if (isActive && !this.isHostingSession) {
          this.liveStream = live
          this._syncHostTimerFromStream(live)
          try {
            const detail = await liveService.getLive(live.id)
            const merged = detail.data ?? detail
            this.liveStream = { ...live, ...merged }
            this._hydratePostAsFromStream(merged)
          } catch {
            this._hydratePostAsFromStream(live)
          }
        } else if (!isActive) {
          this.hasServerActiveLive = false
          this.recoverableLive = null
        }
      } catch { /* silent */ }
    },

    _syncHostTimerFromStream(live) {
      if (!live) return
      const started =
        live.startedAt ||
        (['LIVE', 'RECONNECTING'].includes(live.status) ? live.createdAt : null)
      if (started) {
        this.startedAt = typeof started === 'string' ? started : new Date(started).toISOString()
      } else if (live.status === 'WAITING') {
        this.startedAt = null
      }
    },

    _hydratePostAsFromStream(stream) {
      if (!stream) return
      if (stream.pageId) {
        const page = stream.page || {}
        this.postAsPageId = stream.pageId
        this.postAsPageName = page.name || page.username || this.postAsPageName || null
        this.postAsPageAvatar = page.profilePictureUrl || this.postAsPageAvatar || null
        this.privacySetting = 'PUBLIC'
        return
      }
      // Only clear when the server explicitly says this is a personal live.
      // Partial payloads (e.g. startLive / my-current) omit pageId — do not wipe preview state.
      if (this.isHostingSession && stream.pageId === null) {
        this.postAsPageId = null
        this.postAsPageName = null
        this.postAsPageAvatar = null
      }
    },

    async recoverLive() {
      if (this.phase !== LIVE_PHASES.IDLE && this.phase !== LIVE_PHASES.PREVIEWING) return
      this._setPhase(LIVE_PHASES.RECOVERING)
      this.error = null
      try {
        if (this._suspendedStream?.id && !this.liveStream?.id) {
          this.liveStream = this._suspendedStream
        }
        const res = await liveService.getMyCurrentLive()
        const live = res.data?.live
        if (!live) {
          this.reset()
          return
        }
        if (live.status === 'ENDED') {
          this.hasServerActiveLive = false
          this.recoverableLive = null
          this.liveStream = live
          this.endReason = LIVE_END_REASONS.HOST_ENDED
          this.endStats = {
            totalUniqueViewers: live.totalUniqueViewers ?? 0,
            peakViewerCount: live.peakViewerCount ?? 0,
            totalReactions: live.totalReactions ?? 0,
            duration: live.startedAt && live.endedAt
              ? Math.floor((new Date(live.endedAt) - new Date(live.startedAt)) / 1000)
              : 0,
            hasRecording: live.hasRecording ?? false,
          }
          this._setPhase(LIVE_PHASES.ENDED)
          this.pendingNavigation = 'hostEnd'
          return
        }
        const refreshed = await liveService.refreshStreamerToken(live.id)
        const { token, wsUrl } = refreshed.data
        this.liveStream = live
        this.recoverableLive = live
        this._syncHostTimerFromStream(live)
        try {
          const detail = await liveService.getLive(live.id)
          const merged = detail.data ?? detail
          this._syncHostTimerFromStream(merged)
          this.liveStream = { ...this.liveStream, ...merged }
          this._hydratePostAsFromStream(merged)
        } catch {
          this._hydratePostAsFromStream(live)
        }
        this.token = token
        this.wsUrl = wsUrl
        this.role = 'host'
        this.isHostingSession = true
        this._ensureSocketListeners()
        this._joinSocketRoom(live.id)
        this._setPhase(LIVE_PHASES.LIVEKIT_CONNECTING)
        this._registerLiveKitListeners()
        await liveKit.connectLiveKitAsHost(wsUrl, token, { audioOnly: this.hostLiveMode === 'AUDIO_ONLY' })
        if (this.isAudioMuted) await liveKit.setLiveKitMicrophoneEnabled(false)
        if (this.isVideoOff) await liveKit.setLiveKitCameraEnabled(false)
        this._syncVideoLayoutFromRoom()
        this._setPhase(LIVE_PHASES.CONNECTED_HOST)
        this._scheduleHostVideoSurfaceNudges()
        this.pendingNavigation = 'hostLive'
        this._suspendedStream = null
        await this._resyncCounts(live.id)
        await this._resyncMissedComments(live.id)
      } catch (err) {
        this.error = parseLiveError(err)
        await liveKit.disconnectLiveKit().catch(() => {})
        this.isHostingSession = false
        const preserved = this.recoverableLive || this._suspendedStream
        if (preserved?.id) {
          this.liveStream = preserved
          this.recoverableLive = preserved
          this.hasServerActiveLive = true
          this._syncHostTimerFromStream(preserved)
          this._setPhase(LIVE_PHASES.IDLE)
        } else {
          await this.checkForActiveLive()
          this._setPhase(LIVE_PHASES.IDLE)
        }
      }
    },

  // ─── Start (host) ──────────────────────────────────────────────────────
    async startLive(dto = {}) {
      this.error = null
      this._setPhase(LIVE_PHASES.VALIDATING)
      try {
        const body = { ...dto }
        if (dto.liveMode) this.hostLiveMode = dto.liveMode
        if (this.liveCategory) body.category = this.liveCategory
        if (this.postAsPageId) {
          body.pageId = this.postAsPageId
          body.privacySetting = 'PUBLIC'
        } else if (this.privacySetting) {
          body.privacySetting = this.privacySetting
        }
        const res = await liveService.startLive(body)
        const { liveStream, token, wsUrl } = res.data
        this.liveStream = liveStream
        this._hydratePostAsFromStream(liveStream)
        this.token = token
        this.wsUrl = wsUrl
        this.role = 'host'
        this.isHostingSession = true
        this._setPhase(LIVE_PHASES.SOCKET_CONNECTING)
        this._ensureSocketListeners()
        this._joinSocketRoom(liveStream.id)
        this._setPhase(LIVE_PHASES.LIVEKIT_CONNECTING)
        this._registerLiveKitListeners()
        await liveKit.connectLiveKitAsHost(wsUrl, token, { audioOnly: this.hostLiveMode === 'AUDIO_ONLY' })
        if (this.isAudioMuted) await liveKit.setLiveKitMicrophoneEnabled(false)
        if (this.isVideoOff) await liveKit.setLiveKitCameraEnabled(false)
        this._syncVideoLayoutFromRoom()
        this._setPhase(LIVE_PHASES.CONNECTED_HOST)
        this._scheduleHostVideoSurfaceNudges()
        await this._resyncCounts(liveStream.id)
        await this._resyncMissedComments(liveStream.id)
        return liveStream
      } catch (err) {
        await this._cleanupSession()
        this._setPhase(LIVE_PHASES.ERROR)
        this.error = parseLiveError(err)
        if (this.error === 'ALREADY_ACTIVE') {
          await this.recoverLive()
          return
        }
        throw err
      }
    },

    async goLive() {
      if (!this.liveStream?.id) return
      if (this.liveStream.status === 'LIVE') return
      try {
        await liveService.goLive(this.liveStream.id)
        let serverStartedAt = new Date().toISOString()
        try {
          const res = await liveService.getLive(this.liveStream.id)
          const detail = res.data ?? res
          if (detail?.startedAt) serverStartedAt = detail.startedAt
          this.liveStream = { ...this.liveStream, ...detail, status: 'LIVE', startedAt: serverStartedAt }
          this._hydratePostAsFromStream(detail)
        } catch { /* */ }
        this.startedAt = serverStartedAt
        if (!this.liveStream.startedAt) {
          this.liveStream = { ...this.liveStream, status: 'LIVE', startedAt: serverStartedAt }
        }
      } catch (err) {
        this.error = parseLiveError(err)
        throw err
      }
    },

    async endLive() {
      if (!this.liveStream?.id) return
      this._setPhase(LIVE_PHASES.ENDING)
      const id = this.liveStream.id
      const status = this.liveStream.status
      try {
        if (status === 'WAITING') {
          await liveService.deleteLive(id)
          this.endReason = LIVE_END_REASONS.HOST_ENDED
          this.isHostingSession = false
          this._suspendedStream = null
          this._setPhase(LIVE_PHASES.ENDED)
          this.pendingNavigation = 'closeAll'
        } else {
          const res = await liveService.endLive(id)
          this.endStats = res.data?.stats ?? null
          this.endReason = LIVE_END_REASONS.HOST_ENDED
          this.isHostingSession = false
          this._suspendedStream = null
          this._setPhase(LIVE_PHASES.ENDED)
          this.pendingNavigation = 'hostEnd'
        }
      } catch (err) {
        this._setPhase(LIVE_PHASES.CONNECTED_HOST)
        this.error = parseLiveError(err)
        throw err
      } finally {
        await this._cleanupSession()
      }
    },

    async discardLive() {
      const id = this.liveStream?.id
      if (id) {
        try { await liveService.deleteLive(id) } catch { /* */ }
      }
      await this._cleanupSession()
      this.reset()
    },

    /**
     * Disconnect client media/socket but keep server stream alive for recovery (mobile suspendSession).
     */
    async suspendSession() {
      if (!this.isHostingSession) return
      const preserved = this.liveStream
      const wasActive = this.hasServerActiveLive
      await this._cleanupSession()
      this.isHostingSession = false
      this._suspendedStream = preserved
      this.liveStream = preserved
      this.recoverableLive = preserved
      this._syncHostTimerFromStream(preserved)
      this.hasServerActiveLive = wasActive || !!preserved?.id
      this.role = 'host'
      this.token = null
      this.wsUrl = null
      this.comments = []
      this.pinnedComment = null
      this.guestInvite = null
      this.activeGuests = []
      this.guestProfiles = {}
      this.guestMedia = {}
      this._setPhase(LIVE_PHASES.IDLE)
    },

    async publishLive(saveAsPost) {
      if (!this.liveStream?.id) return
      try {
        await liveService.publishLive(this.liveStream.id, saveAsPost)
      } catch { /* best-effort */ }
      this.reset()
    },

  // ─── Join (viewer) ───────────────────────────────────────────────────
    async joinLive(streamId) {
      if (this.isHostingSession && this.liveStreamId === streamId) {
        this.pendingNavigation = 'hostLive'
        return
      }
      if (this.phase === LIVE_PHASES.ENDED || this.phase === LIVE_PHASES.ERROR) {
        await this._cleanupSession()
        this.reset()
      }
      if (this.phase !== LIVE_PHASES.IDLE) return
      this.error = null
      this._setPhase(LIVE_PHASES.VALIDATING)
      try {
        const res = await liveService.joinLive(streamId)
        const { token, wsUrl, liveStream, blockedUserIds, pinnedComment, host, activeGuests, focusedUserId } = res.data
        this.liveStream = liveStream
        this.token = token
        this.wsUrl = wsUrl
        this.role = 'viewer'
        this.hostInfo = host ?? null
        this.blockedUserIds = new Set(blockedUserIds ?? [])
        this.pinnedComment = pinnedComment ?? null
        this.focusedUserId = focusedUserId ?? null
        this._seedActiveGuests(activeGuests)
        this.viewerCount = liveStream.viewerCount ?? 0
        this._applyStreamCounts(liveStream)
        this._setPhase(LIVE_PHASES.SOCKET_CONNECTING)
        this._ensureSocketListeners()
        this._joinSocketRoom(streamId)
        this._setPhase(LIVE_PHASES.LIVEKIT_CONNECTING)
        this._registerLiveKitListeners()
        await liveKit.connectLiveKitAsViewer(wsUrl, token)
        // Seed the audio-blocked state (the event only fires on later changes).
        this.audioBlocked = !liveKit.isLiveKitAudioAllowed()
        this._syncVideoLayoutFromRoom()
        this._setPhase(LIVE_PHASES.CONNECTED_VIEWER)
        this._scheduleViewerVideoSurfaceNudges()
        this.pendingNavigation = 'viewerLive'
        await this._resyncCounts(streamId)
        await this._resyncMissedComments(streamId)
        return liveStream
      } catch (err) {
        const code = parseLiveError(err)
        this.error = code
        if (code === 'PRIVACY') {
          try {
            const preview = await liveService.getLive(streamId)
            const stream = preview.data ?? preview
            this.liveStream = stream
            this.hostInfo = buildHostInfoFromStream(stream)
          } catch { /* */ }
          this.endReason = LIVE_END_REASONS.PRIVACY
          this._setPhase(LIVE_PHASES.ENDED)
          this.pendingNavigation = 'viewerEnd'
          return
        }
        if (code === 'INVALID_STATE') {
          this.endReason = LIVE_END_REASONS.HOST_ENDED
          this._setPhase(LIVE_PHASES.ENDED)
          this.pendingNavigation = 'viewerEnd'
          return
        }
        await this._cleanupSession()
        this._setPhase(LIVE_PHASES.ERROR)
        throw err
      }
    },

    async leaveLive() {
      const id = this.liveStream?.id
      if (!id) return
      try { await liveService.leaveLive(id) } catch { /* */ }
      await this._cleanupSession()
      this._setPhase(LIVE_PHASES.ENDED)
      this.endReason = LIVE_END_REASONS.HOST_ENDED
    },

  // ─── Media controls ──────────────────────────────────────────────────
    async toggleAudio() {
      this.isAudioMuted = !this.isAudioMuted
      if (this.isInSession) await liveKit.setLiveKitMicrophoneEnabled(!this.isAudioMuted)
    },

    async toggleVideo() {
      this.isVideoOff = !this.isVideoOff
      if (this.isInSession) await liveKit.setLiveKitCameraEnabled(!this.isVideoOff)
    },

    async switchCamera() {
      await liveKit.switchLiveKitCamera()
    },

  // ─── Comments & reactions ────────────────────────────────────────────
    _buildCommentSocketPayload(liveStreamId, content) {
      const payload = { liveStreamId, content: content.trim() }
      const pageId = this.postAsPageId || this.liveStream?.pageId
      if (this.isHostingSession && pageId) payload.asPageId = pageId
      return payload
    },

    sendComment(content, currentUser = null) {
      const id = this.liveStream?.id
      const trimmed = content?.trim()
      if (!id || !trimmed) return
      if (
        currentUser?.id &&
        this.comments.some(
          (c) =>
            c.sendStatus === 'sending' &&
            c.content === trimmed &&
            c.userId === currentUser.id,
        )
      ) {
        return
      }
      const pendingId = `pending_${Date.now()}`
      if (currentUser) {
        const optimistic = {
          id: pendingId,
          liveStreamId: id,
          userId: currentUser.id,
          content: trimmed,
          createdAt: new Date().toISOString(),
          user: currentUser,
          sendStatus: 'sending',
        }
        const pageId = this.postAsPageId || this.liveStream?.pageId
        if (this.isHostingSession && pageId) {
          optimistic.pageId = pageId
          optimistic.isOwner = true
          optimistic.authorRole = 'owner'
          optimistic.page = {
            id: pageId,
            name: this.postAsPageName || this.liveStream?.page?.name,
            profilePictureUrl: this.postAsPageAvatar || this.liveStream?.page?.profilePictureUrl,
          }
        }
        this.comments = [...this.comments.slice(-(LIVE_DEFAULTS.maxChatMessages - 1)), optimistic]
      }
      const payload = this._buildCommentSocketPayload(id, trimmed)
      const emitted = this._emitIfReady('live:comment', payload)
      if (!emitted) {
        this._pendingComments.push(payload)
        this._schedulePendingCommentsRetry()
      }
    },

    sendReaction(type = 'heart') {
      const now = Date.now()
      if (now - this._lastReactionAt < LIVE_TIMEOUTS.reactionThrottleMs) return
      this._lastReactionAt = now
      const id = this.liveStream?.id
      if (!id) return
      this.likeCount = (this.likeCount ?? 0) + 1
      this._emitIfReady('live:reaction', { liveStreamId: id, type })
      this._addFloatingReaction(type)
    },

    sendTyping() {
      const id = this.liveStream?.id
      if (!id || !this.isInSession) return
      const now = Date.now()
      if (now - this._lastTypingAt < 2000) return
      this._lastTypingAt = now
      this._emitIfReady('live:typing', { liveStreamId: id })
    },

  // ─── Settings (debounced) ────────────────────────────────────────────
    updateSettings(dto) {
      if (!this.liveStream?.id) return Promise.resolve()
      const prev = { ...this.liveStream }
      if (!this._settingsRollback) this._settingsRollback = prev
      const patch = { ...dto }
      if (patch.commentMode !== undefined) {
        patch.commentsEnabled = patch.commentMode !== 'NONE'
      }
      this.liveStream = { ...this.liveStream, ...patch }
      this._pendingSettings = { ...(this._pendingSettings || {}), ...dto }
      if (!this._settingsFlushPromise) {
        this._settingsFlushPromise = new Promise((resolve, reject) => {
          this._settingsResolve = resolve
          this._settingsReject = reject
        })
      }
      clearTimeout(this._settingsTimer)
      this._settingsTimer = setTimeout(() => this._flushSettings(), LIVE_TIMEOUTS.settingsDebounceMs)
      return this._settingsFlushPromise
    },

    async _flushSettings() {
      const id = this.liveStream?.id
      const payload = this._pendingSettings
      const rollback = this._settingsRollback
      this._pendingSettings = null
      this._settingsRollback = null
      const resolve = this._settingsResolve
      const reject = this._settingsReject
      this._settingsFlushPromise = null
      this._settingsResolve = null
      this._settingsReject = null
      if (!id || !payload || Object.keys(payload).length === 0) {
        resolve?.()
        return
      }
      try {
        await liveService.updateSettings(id, payload)
        resolve?.()
      } catch (err) {
        if (rollback) this.liveStream = rollback
        reject?.(err)
      }
    },

  // ─── Moderation ──────────────────────────────────────────────────────
    async kickViewer(userId, reason) {
      if (!this.liveStream?.id) return
      await liveService.kickViewer(this.liveStream.id, userId, reason)
    },
    async banViewer(userId, reason) {
      if (!this.liveStream?.id) return
      await liveService.banViewer(this.liveStream.id, userId, reason)
    },
    async muteViewer(userId, durationSeconds) {
      if (!this.liveStream?.id) return
      await liveService.muteViewer(this.liveStream.id, userId, durationSeconds)
    },
    async promoteModerator(userId) {
      if (!this.liveStream?.id) return
      await liveService.promoteModerator(this.liveStream.id, userId)
    },
    async demoteModerator(userId) {
      if (!this.liveStream?.id) return
      await liveService.demoteModerator(this.liveStream.id, userId)
    },
    async unbanViewer(userId) {
      if (!this.liveStream?.id) return
      await liveService.unbanViewer(this.liveStream.id, userId)
    },
    async unmuteViewer(userId) {
      if (!this.liveStream?.id) return
      await liveService.unmuteViewer(this.liveStream.id, userId)
    },
    async reportComment(commentId, reason) {
      if (!this.liveStream?.id) return
      await liveService.reportComment(this.liveStream.id, commentId, reason)
    },
    async requestToSpeak() {
      if (!this.liveStream?.id) return
      await liveService.requestToSpeak(this.liveStream.id)
      this.speakRequestPending = true
    },
    async acceptSpeakRequest(userId) {
      if (!this.liveStream?.id) return
      await liveService.acceptSpeakRequest(this.liveStream.id, userId)
      this.speakRequests = this.speakRequests.filter((r) => r.userId !== userId)
    },
    async rejectSpeakRequest(userId) {
      if (!this.liveStream?.id) return
      await liveService.rejectSpeakRequest(this.liveStream.id, userId)
      this.speakRequests = this.speakRequests.filter((r) => r.userId !== userId)
    },
    async fetchSpeakRequests() {
      if (!this.liveStream?.id) return
      try {
        const res = await liveService.getSpeakRequests(this.liveStream.id)
        const list = res.data?.requests ?? res.data ?? []
        this.speakRequests = Array.isArray(list) ? list : []
      } catch { /* owner-only / not available — leave as-is */ }
    },
    async addReminder(liveStreamId) {
      await liveService.addReminder(liveStreamId)
    },
    async removeReminder(liveStreamId) {
      await liveService.removeReminder(liveStreamId)
    },
    async getReminderCount(liveStreamId) {
      try {
        const res = await liveService.getReminderCount(liveStreamId)
        return res.data?.count ?? 0
      } catch {
        return 0
      }
    },
    async pinComment(commentId) {
      if (!this.liveStream?.id) return
      await liveService.pinComment(this.liveStream.id, commentId)
    },
    async unpinComment(commentId) {
      if (!this.liveStream?.id) return
      await liveService.unpinComment(this.liveStream.id, commentId)
    },
    async deleteComment(commentId) {
      if (!this.liveStream?.id) return
      await liveService.deleteComment(this.liveStream.id, commentId)
    },
    async reportLive(reason) {
      if (!this.liveStream?.id) return 'missing'
      try {
        await liveService.reportLive(this.liveStream.id, reason)
        return 'success'
      } catch (err) {
        if (parseLiveError(err) === 'ALREADY_REPORTED') return 'alreadyReported'
        return 'failed'
      }
    },
    async shareLive(conversationIds) {
      if (!this.liveStream?.id) return
      await liveService.shareLive(this.liveStream.id, conversationIds)
    },
    async getViewersList(page = 1, search = '') {
      if (!this.liveStream?.id) return []
      const params = { page, limit: LIVE_DEFAULTS.defaultViewersLimit }
      const term = search?.trim()
      if (term) params.search = term
      const res = await liveService.getViewers(this.liveStream.id, params)
      const raw = res.data?.data ?? res.data ?? []
      const list = Array.isArray(raw) ? raw : []
      return list.map(normalizeViewerListItem)
    },
    async getBannedUsersList(page = 1) {
      if (!this.liveStream?.id) return []
      const res = await liveService.getBannedUsers(this.liveStream.id, {
        page,
        limit: LIVE_DEFAULTS.defaultViewersLimit,
      })
      const raw = res.data?.data ?? res.data ?? []
      const list = Array.isArray(raw) ? raw : []
      return list.map(normalizeViewerListItem)
    },

  // ─── Guests ──────────────────────────────────────────────────────────
    /**
     * Seed the on-stage guest roster + profile labels from a join/detail payload
     * (`activeGuests: [{ id, username, firstName, lastName, profilePictureUrl }]`).
     * Without this, a viewer who joins AFTER a guest is already on stage would
     * have an empty roster and never render the existing guests (the
     * `live:guest:joined` event only covers guests who join later).
     */
    _seedActiveGuests(activeGuests) {
      if (!Array.isArray(activeGuests)) return
      const ids = []
      const profiles = { ...this.guestProfiles }
      for (const g of activeGuests) {
        const id = g?.id ?? g?.userId
        if (!id) continue
        ids.push(id)
        const displayName =
          [g.firstName, g.lastName].filter(Boolean).join(' ').trim() ||
          g.username ||
          null
        profiles[id] = {
          displayName,
          username: g.username ?? null,
          profilePictureUrl: g.profilePictureUrl ?? null,
        }
      }
      this.activeGuests = ids
      this.guestProfiles = profiles
      this.videoLayoutEpoch += 1
    },

    async inviteGuest(userId) {
      if (!this.liveStream?.id) return
      await liveService.inviteGuest(this.liveStream.id, userId)
    },
    async declineGuestInvite() {
      if (!this.liveStream?.id) return
      try {
        await liveService.declineGuestInvite(this.liveStream.id)
      } catch {
        /* best-effort — clear local invite either way */
      }
      this.speakRequestPending = false
      this.guestInvite = null
    },
    async acceptGuestInvite() {
      if (!this.liveStream?.id) return
      // The accept endpoint mints a GUEST publish token (role=guest, canPublish).
      // Use it directly — do NOT call refreshStreamerToken, which is owner-only
      // (findOwnedLiveStream) and 403s for a guest, leaving them unable to
      // publish (the old bug where web guests never appeared on stage).
      const res = await liveService.acceptGuestInvite(this.liveStream.id)
      const { token, wsUrl } = res.data ?? {}
      if (!token || !wsUrl) return
      this.speakRequestPending = false
      this.guestInvite = null
      try {
        this.token = token
        this.wsUrl = wsUrl
        await liveKit.disconnectLiveKit()
        // Reconnect as a publisher (camera + mic). audioOnly honours the host's
        // mode; for a guest joining a video live this publishes their camera.
        await liveKit.connectLiveKitAsHost(wsUrl, token, { audioOnly: this.hostLiveMode === 'AUDIO_ONLY' })
        if (this.isAudioMuted) await liveKit.setLiveKitMicrophoneEnabled(false)
        if (this.isVideoOff) await liveKit.setLiveKitCameraEnabled(false)
        this._registerLiveKitListeners()
        this.videoLayoutEpoch += 1
      } catch { /* */ }
    },
    async removeGuest(userId) {
      if (!this.liveStream?.id) return
      await liveService.removeGuest(this.liveStream.id, userId)
    },
    // Host-only per-guest track controls. Optimistically patch local state; the
    // `live:guest:audio|video:*` room events confirm/correct it for everyone.
    async muteGuestAudio(userId) {
      if (!this.liveStream?.id) return
      this._onGuestMedia(userId, { audioMuted: true })
      await liveService.muteGuestAudio(this.liveStream.id, userId)
    },
    async unmuteGuestAudio(userId) {
      if (!this.liveStream?.id) return
      this._onGuestMedia(userId, { audioMuted: false })
      await liveService.unmuteGuestAudio(this.liveStream.id, userId)
    },
    async muteGuestVideo(userId) {
      if (!this.liveStream?.id) return
      this._onGuestMedia(userId, { videoMuted: true })
      await liveService.muteGuestVideo(this.liveStream.id, userId)
    },
    async unmuteGuestVideo(userId) {
      if (!this.liveStream?.id) return
      this._onGuestMedia(userId, { videoMuted: false })
      await liveService.unmuteGuestVideo(this.liveStream.id, userId)
    },

  // ─── Post-as / privacy ───────────────────────────────────────────────
    setPostAsPage(pageId, pageName, pageAvatar = null) {
      this.postAsPageId = pageId
      this.postAsPageName = pageName
      this.postAsPageAvatar = pageAvatar
      if (pageId) this.privacySetting = 'PUBLIC'
    },
    clearPostAsPage() {
      this.postAsPageId = null
      this.postAsPageName = null
      this.postAsPageAvatar = null
    },
    setPrivacy(setting) {
      this.privacySetting = setting === 'PRIVATE' ? 'PUBLIC' : setting
    },
    setCategory(category) {
      this.liveCategory = category || 'CHAT'
    },

  // ─── Feed ────────────────────────────────────────────────────────────
    async fetchFeed(params = {}) {
      this.feedLoading = true
      const query = {
        sort: this.feedSort,
        category: this.feedCategory || undefined,
        limit: LIVE_DEFAULTS.defaultFeedLimit,
        ...params,
      }
      try {
        const res = await liveService.getActiveFeed(query)
        const data = res.data?.data ?? []
        if (params.page && params.page > 1) {
          this.feedItems = [...this.feedItems, ...data]
        } else {
          this.feedItems = data
        }
        const limit = params.limit ?? LIVE_DEFAULTS.defaultFeedLimit
        this.feedPagination = {
          page: params.page ?? 1,
          hasMore: data.length >= limit,
        }
      } catch { /* */ } finally {
        this.feedLoading = false
      }
    },

    async fetchFollowingLive() {
      try {
        const res = await liveService.getFollowingLive()
        this.followingLiveItems = normalizeFollowingLiveItems(res)
      } catch { /* */ }
    },

    async checkUserLive(userId) {
      try {
        const res = await liveService.checkUserLive(userId)
        return res.data
      } catch {
        return { isLive: false, liveStreamId: null }
      }
    },

    clearPendingNavigation() {
      this.pendingNavigation = null
    },

  // ─── Socket ──────────────────────────────────────────────────────────
    _emitIfReady(event, data) {
      if (!WebSocketManager.isConnected()) return false
      WebSocketManager.emit(event, data).catch(() => {})
      return true
    },

    _joinSocketRoom(liveStreamId) {
      this._emitIfReady('live:join', { liveStreamId })
    },

    _leaveSocketRoom(liveStreamId) {
      WebSocketManager.emit('live:leave', { liveStreamId }).catch(() => {})
    },

    _ensureSocketListeners() {
      if (this._socketHandlersBound) return
      const refs = {
        commentNew: (d) => {
          if (!this._dedupSocketEvent(d, 'live:comment:new')) this._onCommentNew(d)
        },
        commentPinned: (d) => this._onCommentPinned(d),
        commentDeleted: (d) => this._onCommentDeleted(d),
        viewerJoined: (d) => this._onViewerJoined(d),
        viewerLeft: (d) => this._onViewerLeft(d),
        layoutUpdate: (d) => this._onLayoutUpdate(d),
        liveTyping: () => { /* optional typing indicator — no UI yet */ },
        started: (d) => {
          if (!this._dedupSocketEvent(d, 'live:started')) this._onLiveStarted(d)
        },
        feedLivePostCreated: (d) => {
          if (!this._dedupSocketEvent(d, 'feed:live-post-created')) this._onLiveStarted(d)
        },
        ended: (d) => {
          if (!this._dedupSocketEvent(d, 'live:ended')) this._onStreamEnded(d)
        },
        streamerDisconnected: () => this._onStreamerDisconnected(),
        streamerReconnected: () => this._onStreamerReconnected(),
        settingsUpdated: (d) => this._onSettingsUpdated(d),
        kicked: (d) => this._onViewerKickedBroadcast(d),
        banned: (d) => this._onViewerBannedBroadcast(d),
        muted: (d) => this._onViewerMutedBroadcast(d),
        unmuted: (d) => this._onViewerUnmutedBroadcast(d),
        selfKicked: () => this._onKicked(),
        selfBanned: () => this._onBanned(),
        selfMuted: (d) => this._onMuted(d),
        selfUnmuted: () => this._onUnmuted(),
        reaction: (d) => this._onReaction(d),
        maxDurationWarning: (d) => this._onMaxDurationWarning(d),
        maxDurationReached: () => this._onMaxDurationReached(),
        guestInvite: (d) => {
          this.guestInvite = d;
          if (d?.fromSpeakRequest) this.speakRequestPending = false;
        },
        guestForceMuted: (d) => this._onGuestForceMuted(d),
        speakRequest: (d) => this._onSpeakRequest(d),
        speakRequestRemoved: (d) => this._onSpeakRequestRemoved(d),
        speakRejected: (d) => this._onSpeakRejected(d),
        guestDeclined: (d) => this._onGuestDeclined(d),
        terminatedByModerator: (d) => this._onTerminatedByModerator(d),
        recordingFailed: (d) => this._onRecordingFailed(d),
        moderatorPromoted: (d) => this._onModeratorRoleChange(d),
        moderatorDemoted: (d) => this._onModeratorRoleChange(d),
        unbanned: (d) => this._onUnbanned(d),
        guestTokenRefresh: async () => {
          if (!this.isHostingSession || !this.liveStream?.id) return
          try {
            const res = await liveService.refreshStreamerToken(this.liveStream.id)
            const { token, wsUrl } = res.data
            if (token && wsUrl) {
              this.token = token
              this.wsUrl = wsUrl
              await liveKit.disconnectLiveKit()
              await liveKit.connectLiveKitAsHost(wsUrl, token, { audioOnly: this.hostLiveMode === 'AUDIO_ONLY' })
              this._registerLiveKitListeners()
              this.videoLayoutEpoch += 1
            }
          } catch { /* */ }
        },
        milestone: (d) => this._onMilestone(d),
        guestJoined: (d) => this._onGuestJoined(d),
        guestRemoved: (d) => this._onGuestRemoved(d),
        guestAudioMuted: (d) => this._onGuestMedia(d?.userId, { audioMuted: true }),
        guestAudioUnmuted: (d) => this._onGuestMedia(d?.userId, { audioMuted: false }),
        guestVideoMuted: (d) => this._onGuestMedia(d?.userId, { videoMuted: true }),
        guestVideoUnmuted: (d) => this._onGuestMedia(d?.userId, { videoMuted: false }),
      }
      this._socketHandlerRefs = refs
      WebSocketManager.on('live:comment:new', refs.commentNew)
      WebSocketManager.on('live:comment:pinned', refs.commentPinned)
      WebSocketManager.on('live:comment:deleted', refs.commentDeleted)
      WebSocketManager.on('live:viewer:joined', refs.viewerJoined)
      WebSocketManager.on('live:viewer:left', refs.viewerLeft)
      WebSocketManager.on('live:layout:update', refs.layoutUpdate)
      WebSocketManager.on('live:typing', refs.liveTyping)
      WebSocketManager.on('live:ended', refs.ended)
      WebSocketManager.on('live:streamer:disconnected', refs.streamerDisconnected)
      WebSocketManager.on('live:streamer:reconnected', refs.streamerReconnected)
      WebSocketManager.on('live:settings:updated', refs.settingsUpdated)
      WebSocketManager.on('live:kicked', refs.selfKicked)
      WebSocketManager.on('live:banned', refs.selfBanned)
      WebSocketManager.on('live:muted', refs.selfMuted)
      WebSocketManager.on('live:unmuted', refs.selfUnmuted)
      WebSocketManager.on('live:viewer:kicked', refs.kicked)
      WebSocketManager.on('live:viewer:banned', refs.banned)
      WebSocketManager.on('live:viewer:muted', refs.muted)
      WebSocketManager.on('live:viewer:unmuted', refs.unmuted)
      WebSocketManager.on('live:reaction', refs.reaction)
      WebSocketManager.on('live:max-duration-warning', refs.maxDurationWarning)
      WebSocketManager.on('live:max-duration-reached', refs.maxDurationReached)
      WebSocketManager.on('live:started', refs.started)
      WebSocketManager.on('feed:live-post-created', refs.feedLivePostCreated)
      WebSocketManager.on('live:guest:invite', refs.guestInvite)
      WebSocketManager.on('live:guest:force-muted', refs.guestForceMuted)
      WebSocketManager.on('live:speak:request', refs.speakRequest)
      WebSocketManager.on('live:speak:request:removed', refs.speakRequestRemoved)
      WebSocketManager.on('live:speak:rejected', refs.speakRejected)
      WebSocketManager.on('live:guest:declined', refs.guestDeclined)
      WebSocketManager.on('live:terminated-by-moderator', refs.terminatedByModerator)
      WebSocketManager.on('live:recording:failed', refs.recordingFailed)
      WebSocketManager.on('live:moderator:promoted', refs.moderatorPromoted)
      WebSocketManager.on('live:moderator:demoted', refs.moderatorDemoted)
      WebSocketManager.on('live:unbanned', refs.unbanned)
      WebSocketManager.on('live:guest:token:refresh', refs.guestTokenRefresh)
      WebSocketManager.on('live:milestone', refs.milestone)
      WebSocketManager.on('live:guest:joined', refs.guestJoined)
      WebSocketManager.on('live:guest:removed', refs.guestRemoved)
      WebSocketManager.on('live:guest:audio:muted', refs.guestAudioMuted)
      WebSocketManager.on('live:guest:audio:unmuted', refs.guestAudioUnmuted)
      WebSocketManager.on('live:guest:video:muted', refs.guestVideoMuted)
      WebSocketManager.on('live:guest:video:unmuted', refs.guestVideoUnmuted)
      this._socketHandlersBound = true
      if (!this._socketStateUnsub) {
        this._socketStateUnsub = WebSocketManager.onStateChange(() => {
          if (WebSocketManager.isConnected() && this.isInSession && this.liveStreamId) {
            this._joinSocketRoom(this.liveStreamId)
            this._resyncCounts(this.liveStreamId)
            this._resyncMissedComments(this.liveStreamId)
            this._flushPendingComments()
          }
        })
      }
    },

    setupSocketListeners() {
      this._ensureSocketListeners()
    },

    teardownSocketListeners() {
      const refs = this._socketHandlerRefs
      if (!refs || !this._socketHandlersBound) return
      WebSocketManager.off('live:comment:new', refs.commentNew)
      WebSocketManager.off('live:comment:pinned', refs.commentPinned)
      WebSocketManager.off('live:comment:deleted', refs.commentDeleted)
      WebSocketManager.off('live:viewer:joined', refs.viewerJoined)
      WebSocketManager.off('live:viewer:left', refs.viewerLeft)
      WebSocketManager.off('live:layout:update', refs.layoutUpdate)
      WebSocketManager.off('live:typing', refs.liveTyping)
      WebSocketManager.off('live:ended', refs.ended)
      WebSocketManager.off('live:streamer:disconnected', refs.streamerDisconnected)
      WebSocketManager.off('live:streamer:reconnected', refs.streamerReconnected)
      WebSocketManager.off('live:settings:updated', refs.settingsUpdated)
      WebSocketManager.off('live:kicked', refs.selfKicked)
      WebSocketManager.off('live:banned', refs.selfBanned)
      WebSocketManager.off('live:muted', refs.selfMuted)
      WebSocketManager.off('live:unmuted', refs.selfUnmuted)
      WebSocketManager.off('live:viewer:kicked', refs.kicked)
      WebSocketManager.off('live:viewer:banned', refs.banned)
      WebSocketManager.off('live:viewer:muted', refs.muted)
      WebSocketManager.off('live:viewer:unmuted', refs.unmuted)
      WebSocketManager.off('live:reaction', refs.reaction)
      WebSocketManager.off('live:max-duration-warning', refs.maxDurationWarning)
      WebSocketManager.off('live:max-duration-reached', refs.maxDurationReached)
      WebSocketManager.off('live:started', refs.started)
      WebSocketManager.off('feed:live-post-created', refs.feedLivePostCreated)
      WebSocketManager.off('live:guest:invite', refs.guestInvite)
      WebSocketManager.off('live:guest:force-muted', refs.guestForceMuted)
      WebSocketManager.off('live:speak:request', refs.speakRequest)
      WebSocketManager.off('live:speak:request:removed', refs.speakRequestRemoved)
      WebSocketManager.off('live:speak:rejected', refs.speakRejected)
      WebSocketManager.off('live:guest:declined', refs.guestDeclined)
      WebSocketManager.off('live:terminated-by-moderator', refs.terminatedByModerator)
      WebSocketManager.off('live:recording:failed', refs.recordingFailed)
      WebSocketManager.off('live:moderator:promoted', refs.moderatorPromoted)
      WebSocketManager.off('live:moderator:demoted', refs.moderatorDemoted)
      WebSocketManager.off('live:unbanned', refs.unbanned)
      WebSocketManager.off('live:guest:token:refresh', refs.guestTokenRefresh)
      WebSocketManager.off('live:milestone', refs.milestone)
      WebSocketManager.off('live:guest:joined', refs.guestJoined)
      WebSocketManager.off('live:guest:removed', refs.guestRemoved)
      WebSocketManager.off('live:guest:audio:muted', refs.guestAudioMuted)
      WebSocketManager.off('live:guest:audio:unmuted', refs.guestAudioUnmuted)
      WebSocketManager.off('live:guest:video:muted', refs.guestVideoMuted)
      WebSocketManager.off('live:guest:video:unmuted', refs.guestVideoUnmuted)
      this._socketHandlersBound = false
      if (typeof this._socketStateUnsub === 'function') {
        this._socketStateUnsub()
        this._socketStateUnsub = null
      }
    },

    _onCommentNew(data) {
      if (!data) return
      const comment = data.comment ?? data
      if (!comment?.id) return
      if (data.liveStreamId && data.liveStreamId !== this.liveStream?.id) return
      if (this.blockedUserIds.has(comment.userId)) return
      if (typeof data.commentCount === 'number') this.commentCount = data.commentCount
      this._mergeIncomingComment(comment, {
        isOwner: data.isOwner ?? false,
        role: data.role ?? 'VIEWER',
      })
    },

    _onCommentPinned(data) {
      if (!data?.commentId) return
      if (data.isPinned === false) {
        if (this.pinnedComment?.id === data.commentId) this.pinnedComment = null
        return
      }
      const comment = this.comments.find((c) => c.id === data.commentId)
      if (comment) this.pinnedComment = comment
    },

    _onCommentDeleted(data) {
      this.comments = this.comments.filter((c) => c.id !== data?.commentId)
      if (this.pinnedComment?.id === data?.commentId) this.pinnedComment = null
      if (this.commentCount > 0) this.commentCount -= 1
    },

    _onLayoutUpdate(data) {
      if (!data) return
      if (data.focusedUserId !== undefined) this.focusedUserId = data.focusedUserId
      this.videoLayoutEpoch += 1
    },

    _onViewerJoined(data) {
      if (!data) return
      if (typeof data.viewerCount === 'number') {
        this.viewerCount = data.viewerCount
      }
      const user = data.user ?? data
      const entry = {
        id: Date.now(),
        userId: user.id ?? user.userId ?? data.userId,
        username: user.username ?? user.displayName ?? user.firstName,
        avatar: user.profilePictureUrl ?? user.avatar,
      }
      if (entry.userId || entry.username) {
        this.recentJoins = [...this.recentJoins.slice(-4), entry]
        setTimeout(() => { this.recentJoins = this.recentJoins.filter((j) => j.id !== entry.id) }, 4000)
      }
    },

    _onViewerLeft(data) {
      if (!data) return
      if (typeof data.viewerCount === 'number') {
        this.viewerCount = data.viewerCount
      }
    },

    _onStreamEnded(data) {
      const endedId = data?.liveStreamId ?? data?.id
      if (endedId) {
        this.feedItems = this.feedItems.filter((s) => s.id !== endedId)
        this.followingLiveItems = this.followingLiveItems.filter((i) => i.liveStreamId !== endedId)
      } else if (!this.isInSession) {
        this.fetchFollowingLive()
      }
      if (!this.isInSession) return
      if (endedId && this.liveStream?.id && endedId !== this.liveStream.id) return
      this.endReason = LIVE_END_REASONS.HOST_ENDED
      this._setPhase(LIVE_PHASES.ENDED)
      if (this.isViewer) this.pendingNavigation = 'viewerEnd'
      else if (this.isHostingSession) this.pendingNavigation = 'hostEnd'
      this._cleanupSession()
    },

    _onStreamerDisconnected() {
      if (this.isViewer) {
        this.isHostVideoOff = true
        this._setPhase(LIVE_PHASES.RECONNECTING)
      }
    },

    _onStreamerReconnected() {
      if (this.phase === LIVE_PHASES.RECONNECTING) {
        this.isHostVideoOff = false
        this._setPhase(LIVE_PHASES.CONNECTED_VIEWER)
        this.videoLayoutEpoch += 1
      }
    },

    _onSettingsUpdated(data) {
      if (this.liveStream && data) {
        const commentMode = data.commentMode ?? this.liveStream.commentMode
        this.liveStream = {
          ...this.liveStream,
          ...data,
          commentsEnabled: commentMode !== 'NONE',
          commentMode,
        }
      }
    },

    _onKicked() {
      this.endReason = LIVE_END_REASONS.KICKED
      this._setPhase(LIVE_PHASES.ENDED)
      this.pendingNavigation = 'viewerEnd'
      this._cleanupSession()
    },

    _onViewerKickedBroadcast(data) {
      if (!this.isViewer) return
      const me = useUserStore().getUser()?.id
      if (data?.userId && me && data.userId !== me) return
      this._onKicked()
    },

    _onBanned() {
      this.endReason = LIVE_END_REASONS.BANNED
      this._setPhase(LIVE_PHASES.ENDED)
      this.pendingNavigation = 'viewerEnd'
      this._cleanupSession()
    },

    _onViewerBannedBroadcast(data) {
      if (!this.isViewer) return
      const me = useUserStore().getUser()?.id
      if (data?.userId && me && data.userId !== me) return
      this._onBanned()
    },

    _onMuted(data) {
      this.isMuted = true
      this.mutedRemainingSeconds = data?.durationSeconds ?? 60
    },

    _onViewerMutedBroadcast(data) {
      if (!this.isViewer) return
      const me = useUserStore().getUser()?.id
      if (data?.userId && me && data.userId !== me) return
      this._onMuted(data)
    },

    _onUnmuted() {
      this.isMuted = false
      this.mutedRemainingSeconds = 0
    },

    _onViewerUnmutedBroadcast(data) {
      if (!this.isViewer) return
      const me = useUserStore().getUser()?.id
      if (data?.userId && me && data.userId !== me) return
      this._onUnmuted()
    },

    _onReaction(data) {
      if (!data) return
      if (typeof data.reactionCount === 'number') {
        this.likeCount = data.reactionCount
      } else {
        this.likeCount = (this.likeCount ?? 0) + 1
      }

      const me = useUserStore().getUser()?.id
      const senderId = data.userId
      // Own tap already shows an optimistic floater — skip socket echo to avoid double hearts
      if (senderId && me && senderId === me) return

      const type = data.type ?? 'heart'
      const now = Date.now()
      const throttleKey = senderId || 'unknown'
      if (now - (this._reactionDisplayAt[throttleKey] ?? 0) < 400) return
      this._reactionDisplayAt[throttleKey] = now

      this._addFloatingReaction(type)
    },

    _onMilestone(data) {
      const count = data?.viewerCount ?? data?.milestone ?? data?.count
      if (count == null) return
      this.milestoneMessage = String(count)
      setTimeout(() => {
        if (this.milestoneMessage === String(count)) this.milestoneMessage = null
      }, 4500)
    },

    _onGuestJoined(data) {
      // Backend emits `{ user: {...} }` (per the live guest contract); older
      // shapes used userId/guestUserId — accept all so activeGuests populates.
      const user = data?.user
      const userId = user?.id ?? data?.userId ?? data?.guestUserId
      if (userId && !this.activeGuests.includes(userId)) {
        this.activeGuests = [...this.activeGuests, userId]
      }
      if (userId && user) {
        const displayName =
          [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
          user.username ||
          null
        this.guestProfiles = {
          ...this.guestProfiles,
          [userId]: {
            displayName,
            username: user.username ?? null,
            profilePictureUrl: user.profilePictureUrl ?? null,
          },
        }
      }
      this.videoLayoutEpoch += 1
    },

    _onGuestRemoved(data) {
      const userId = data?.user?.id ?? data?.userId ?? data?.guestUserId
      if (userId) {
        this.activeGuests = this.activeGuests.filter((id) => id !== userId)
        const { [userId]: _drop, ...rest } = this.guestMedia
        this.guestMedia = rest
        const { [userId]: _dropProfile, ...restProfiles } = this.guestProfiles
        this.guestProfiles = restProfiles
      }
      this.videoLayoutEpoch += 1
    },

    _onGuestMedia(userId, patch) {
      if (!userId) return
      this.guestMedia = {
        ...this.guestMedia,
        [userId]: { ...(this.guestMedia[userId] || {}), ...patch },
      }
    },

    _onSpeakRequest(data) {
      const userId = data?.userId
      if (!userId) return
      if (this.liveStream?.id && data.liveStreamId && data.liveStreamId !== this.liveStream.id) return
      if (this.speakRequests.some((r) => r.userId === userId)) return
      this.speakRequests = [
        ...this.speakRequests,
        {
          userId,
          username: data.username,
          firstName: data.firstName,
          lastName: data.lastName,
          profilePictureUrl: data.profilePictureUrl,
          requestedAt: data.requestedAt,
        },
      ]
    },

    _onSpeakRequestRemoved(data) {
      const userId = data?.userId
      if (!userId) return
      this.speakRequests = this.speakRequests.filter((r) => r.userId !== userId)
    },

    _onSpeakRejected(data) {
      if (this.liveStream?.id && data?.liveStreamId && data.liveStreamId !== this.liveStream.id) return
      this.speakRequestPending = false
    },

    _onGuestDeclined(data) {
      if (this.liveStream?.id && data?.liveStreamId && data.liveStreamId !== this.liveStream.id) return
      const userId = data?.userId
      if (userId) {
        this.speakRequests = this.speakRequests.filter((r) => r.userId !== userId)
      }
    },

    _onTerminatedByModerator(data) {
      if (data?.liveStreamId && this.liveStream?.id && data.liveStreamId !== this.liveStream.id) return
      if (!this.isHostingSession && !this.isInSession) return
      this.endReason = LIVE_END_REASONS.ERROR
      this.error = data?.reason ? String(data.reason) : 'MODERATOR_TERMINATED'
      this._setPhase(LIVE_PHASES.ENDED)
      this.pendingNavigation = this.isHostingSession ? 'hostEnd' : 'viewerEnd'
      this._cleanupSession()
    },

    _onRecordingFailed(data) {
      if (data?.liveStreamId && this.liveStream?.id && data.liveStreamId !== this.liveStream.id) return
      if (!this.isHostingSession) return
      this.error = data?.message ? String(data.message) : 'RECORDING_FAILED'
    },

    _onModeratorRoleChange(data) {
      if (this.liveStream?.id && data?.liveStreamId && data.liveStreamId !== this.liveStream.id) return
      this.viewerListRevision += 1
    },

    _onUnbanned(data) {
      if (data?.liveStreamId && this.liveStream?.id && data.liveStreamId !== this.liveStream.id) return
      if (this.endReason === LIVE_END_REASONS.BANNED) {
        this.error = null
      }
      this.viewerListRevision += 1
    },

    // The host muted/unmuted THIS client's guest track. Payload carries only the
    // affected kind: `{ audio: bool }` OR `{ video: bool }` (true = muted).
    // Reflect it into the local mute flags AND the actual LiveKit track so the
    // mic/camera button state matches reality. A transient notice lets the UI
    // tell the guest why their track changed.
    async _onGuestForceMuted(data) {
      if (!data) return
      if (typeof data.audio === 'boolean') {
        this.isAudioMuted = data.audio
        try { await liveKit.setLiveKitMicrophoneEnabled(!data.audio) } catch { /* */ }
      }
      if (typeof data.video === 'boolean') {
        this.isVideoOff = data.video
        try { await liveKit.setLiveKitCameraEnabled(!data.video) } catch { /* */ }
      }
      const kind = typeof data.audio === 'boolean' ? 'audio' : 'video'
      const muted = kind === 'audio' ? data.audio : data.video
      // New object ref each time so watchers fire even on repeated same-kind events.
      this.forceMutedNotice = { kind, muted }
    },

    async fetchUserLiveHistory(userId, params = {}) {
      const res = await liveService.getUserLiveHistory(userId, params)
      return res.data?.data ?? res.data ?? []
    },

    _onMaxDurationWarning(data) {
      this.maxDurationWarning = data?.remainingSeconds ?? data?.minutesRemaining ?? 600
    },

    _onMaxDurationReached() {
      this.endReason = LIVE_END_REASONS.MAX_DURATION
      this._setPhase(LIVE_PHASES.ENDED)
      this.pendingNavigation = 'hostEnd'
      this._cleanupSession()
    },

    _onLiveStarted() {
      this.fetchFollowingLive()
      this.fetchFeed()
    },

    _addFloatingReaction(type) {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      const item = {
        id,
        type,
        x: 8 + Math.random() * 72,
        scale: 0.85 + Math.random() * 0.35,
        drift: -12 + Math.random() * 24,
      }
      this.floatingReactions = [
        ...this.floatingReactions.slice(-(LIVE_DEFAULTS.maxFloatingReactions - 1)),
        item,
      ]
      setTimeout(() => {
        this.floatingReactions = this.floatingReactions.filter((r) => r.id !== id)
      }, 2800)
    },

    _flushPendingComments() {
      if (!this._pendingComments.length) return
      const pending = [...this._pendingComments]
      this._pendingComments = []
      for (const p of pending) {
        const ok = this._emitIfReady('live:comment', p)
        if (!ok) this._pendingComments.push(p)
      }
      if (this._pendingComments.length) this._schedulePendingCommentsRetry()
    },

    _schedulePendingCommentsRetry() {
      if (this._pendingCommentRetryTimer) return
      if (this._pendingCommentRetryAttempt >= LIVE_TIMEOUTS.pendingCommentMaxRetries) return
      this._pendingCommentRetryAttempt += 1
      const delay = Math.min(1000 * this._pendingCommentRetryAttempt, 8000)
      this._pendingCommentRetryTimer = setTimeout(() => {
        this._pendingCommentRetryTimer = null
        if (this.isInSession) this._flushPendingComments()
      }, delay)
    },

    _applyStreamCounts(stream) {
      if (!stream) return
      this.likeCount = stream.totalReactions ?? stream.reactionCount ?? this.likeCount ?? 0
      this.commentCount = stream.totalComments ?? stream.commentCount ?? this.commentCount ?? 0
    },

    async _resyncCounts(liveStreamId) {
      try {
        const res = await liveService.getLive(liveStreamId)
        if (this.liveStreamId !== liveStreamId) return
        const s = res.data ?? res
        this._applyStreamCounts(s)
        this.viewerCount = s.viewerCount ?? this.viewerCount
        this.liveStream = { ...this.liveStream, ...s }
        if (Array.isArray(s.activeGuests)) this._seedActiveGuests(s.activeGuests)
        if (this.isHostingSession) {
          this._hydratePostAsFromStream(s)
        } else if (!this.hostInfo && (s.page || s.pageId)) {
          this.hostInfo = buildHostInfoFromStream(s)
        }
      } catch { /* */ }
    },

    async _resyncMissedComments(liveStreamId) {
      try {
        const res = await liveService.getComments(liveStreamId, { limit: LIVE_DEFAULTS.defaultCommentsLimit })
        const incoming = res.data?.comments ?? []
        for (const comment of incoming) {
          if (!comment?.id) continue
          const alreadyVisible = this.comments.some((c) => c.id === comment.id)
          if (alreadyVisible) continue
          const hasPendingTwin = this.comments.some((c) => this._commentMatchesPendingOptimistic(c, comment))
          if (hasPendingTwin) continue
          this._mergeIncomingComment(comment)
        }
      } catch { /* */ }
    },

    _syncVideoLayoutFromRoom() {
      const hasVideo = this.isHostingSession
        ? !!liveKit.getLocalVideoTrack()
        : !!liveKit.getRemoteVideoTrack(this.hostParticipantIdentity)
      if (hasVideo) {
        this.isHostVideoOff = false
        this.videoLayoutEpoch += 1
      }
    },

    _scheduleViewerVideoSurfaceNudges() {
      const id = this.liveStreamId
      if (!id || this.role !== 'viewer') return
      for (const ms of [50, 200, 500]) {
        setTimeout(() => {
          if (this.liveStreamId !== id) return
          if (this.phase !== LIVE_PHASES.CONNECTED_VIEWER) return
          this._syncVideoLayoutFromRoom()
        }, ms)
      }
    },

    _scheduleHostVideoSurfaceNudges() {
      const id = this.liveStreamId
      if (!id || !this.isHostingSession) return
      for (const ms of [50, 200, 500]) {
        setTimeout(() => {
          if (this.liveStreamId !== id) return
          if (this.phase !== LIVE_PHASES.CONNECTED_HOST) return
          if (!this.isHostingSession) return
          this._syncVideoLayoutFromRoom()
        }, ms)
      }
    },

    _registerLiveKitListeners() {
      for (const unsub of this._liveKitUnsubs) unsub()
      this._liveKitUnsubs = [
        liveKit.onLiveKitEvent('disconnected', () => this._onLiveKitDisconnected()),
        liveKit.onLiveKitEvent('reconnecting', () => this._onLiveKitReconnecting()),
        liveKit.onLiveKitEvent('reconnected', () => this._onLiveKitReconnected()),
        liveKit.onLiveKitEvent('trackSubscribed', () => {
          // Bump unconditionally so the main stage AND every guest tile re-bind
          // when a new participant's track arrives (host or guest).
          this.videoLayoutEpoch += 1
          this._syncVideoLayoutFromRoom()
        }),
        liveKit.onLiveKitEvent('trackUnsubscribed', () => {
          if (this.phase === LIVE_PHASES.RECONNECTING) return
          if (!liveKit.getRemoteVideoTrack(this.hostParticipantIdentity)) {
            this.isHostVideoOff = this.isViewer
          }
          this.videoLayoutEpoch += 1
          this._syncVideoLayoutFromRoom()
        }),
        // Browser blocked/allowed remote-audio autoplay → drive the prompt.
        liveKit.onLiveKitEvent('audioPlaybackChanged', (allowed) => {
          this.audioBlocked = !allowed
        }),
      ]
    },

    /** Resume audio from a user tap (autoplay policy). Clears the prompt on success. */
    async enableLiveAudio() {
      const allowed = await liveKit.startLiveKitAudio()
      this.audioBlocked = !allowed
    },

    _onLiveKitDisconnected() {
      if (this.phase === LIVE_PHASES.ENDING || this.phase === LIVE_PHASES.ENDED) return
      this.endReason = LIVE_END_REASONS.NETWORK
      this._setPhase(LIVE_PHASES.ENDED)
      this.pendingNavigation = this.isHostingSession ? 'hostEnd' : 'viewerEnd'
      this._cleanupSession()
    },

    _onLiveKitReconnecting() {
      if (this.phase !== LIVE_PHASES.ENDING && this.phase !== LIVE_PHASES.ENDED) {
        this._setPhase(LIVE_PHASES.RECONNECTING)
        clearTimeout(this._reconnectTimer)
        this._reconnectTimer = setTimeout(() => {
          if (this.phase !== LIVE_PHASES.RECONNECTING) return
          if (liveKit.isLiveKitConnected()) this._onLiveKitReconnected()
          else this._onLiveKitDisconnected()
        }, LIVE_TIMEOUTS.reconnectTimeoutMs)
      }
    },

    _onLiveKitReconnected() {
      clearTimeout(this._reconnectTimer)
      this._reconnectTimer = null
      if (this.phase === LIVE_PHASES.ENDING || this.phase === LIVE_PHASES.ENDED) return
      this._setPhase(this.isHostingSession ? LIVE_PHASES.CONNECTED_HOST : LIVE_PHASES.CONNECTED_VIEWER)
      this._syncVideoLayoutFromRoom()
      if (this.isHostingSession) this._scheduleHostVideoSurfaceNudges()
      else this._scheduleViewerVideoSurfaceNudges()
    },

    async _cleanupSession() {
      this._stopTokenRefresh()
      clearTimeout(this._settingsTimer)
      clearTimeout(this._reconnectTimer)
      clearTimeout(this._pendingCommentRetryTimer)
      const id = this.liveStream?.id
      if (id && this._pendingSettings) {
        try { await this._flushSettings() } catch { /* */ }
      }
      if (id) this._leaveSocketRoom(id)
      for (const unsub of this._liveKitUnsubs) unsub()
      this._liveKitUnsubs = []
      await liveKit.disconnectLiveKit()
      this._pendingComments = []
      this._pendingCommentRetryAttempt = 0
    },

    reset() {
      this.teardownSocketListeners()
      this._cleanupSession()
      this.isHostingSession = false
      liveSocketKeepAlive.clear()
      Object.assign(this, createInitialState())
    },

    async resetSessionAndRestoreFeed() {
      this.reset()
      this.setupSocketListeners()
      await this.fetchFollowingLive()
    },

    clearError() {
      this.error = null
    },

    _parseError: parseLiveError,
  },
})
